import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Tag, Plus, Trash } from "@phosphor-icons/react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";

const PALETTE = ["#C65D47", "#8A9A86", "#D4A373", "#6E8B9E", "#B0413E", "#A4B19F", "#B98455"];

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [color, setColor] = useState(PALETTE[0]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [c, i] = await Promise.all([api.get("/categories"), api.get("/items")]);
    setCats(c.data); setItems(i.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/categories", { name: name.trim(), color });
      setName(""); setColor(PALETTE[0]);
      toast.success("Category added"); load();
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete category?")) return;
    await api.delete(`/categories/${id}`);
    load();
  };

  return (
    <div className="space-y-8" data-testid="categories-page">
      <div>
        <h1 className="font-heading text-4xl sm:text-5xl text-stoneink-900 font-semibold tracking-tight">Categories</h1>
        <p className="mt-2 text-stoneink-700">Tag your things with a meaningful color.</p>
      </div>

      <form onSubmit={create} className="rounded-3xl border border-sand-300 bg-white p-5 shadow-ambient grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Name</label>
          <input
            data-testid="category-name-input"
            value={name} onChange={(e) => setName(e.target.value)} required
            placeholder="e.g. Tools"
            className="mt-2 w-full h-11 px-4 rounded-xl bg-sand-100 border border-sand-300 focus:border-terracotta-500 outline-none"
          />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Color</label>
          <div className="mt-2 flex gap-2">
            {PALETTE.map((c) => (
              <button
                key={c} type="button" onClick={() => setColor(c)}
                data-testid={`color-${c}`}
                className={`h-9 w-9 rounded-full border-2 transition-all ${color === c ? "border-stoneink-900 scale-110" : "border-white"}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>
        <button data-testid="add-category-btn" disabled={busy} className="h-11 px-5 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium flex items-center gap-2 active:scale-[0.98] disabled:opacity-60">
          <Plus weight="bold" /> Add
        </button>
      </form>

      {cats.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-sand-300 bg-sand-100 p-10 text-center">
          <Tag weight="duotone" size={42} className="mx-auto text-stoneink-500" />
          <div className="mt-3 text-stoneink-700">No categories yet.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cats.map((c, i) => {
            const count = items.filter((it) => it.category_id === c.id).length;
            return (
              <motion.div key={c.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className="rounded-3xl border border-sand-300 bg-white p-5 shadow-ambient hover:shadow-hover transition-all flex items-center justify-between"
                data-testid={`category-card-${c.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="h-11 w-11 rounded-2xl grid place-items-center" style={{ backgroundColor: `${c.color}20`, color: c.color }}>
                    <Tag weight="duotone" size={20} />
                  </div>
                  <div>
                    <div className="font-heading text-lg text-stoneink-900 font-medium">{c.name}</div>
                    <div className="text-sm text-stoneink-500">{count} item{count === 1 ? "" : "s"}</div>
                  </div>
                </div>
                <button onClick={() => remove(c.id)} className="p-2 rounded-lg hover:bg-sand-200 text-stoneink-500 hover:text-destructive" data-testid={`delete-category-${c.id}`}>
                  <Trash size={16} />
                </button>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
